/**
 * useTablePermissionContext — DynamicTable userPermissionContext SSOT
 *
 * JWT(getCurrentUser)에서 userId·permissions를 추출하고
 * DynamicTable TableUserPermissionContext(read/write/delete)로 매핑.
 *
 * @see FinanceArListPage — list page SSOT for AR workbench surface
 */

import { useMemo } from 'react';
import type { BusinessDomain } from '@shared/types';
import type { InternalEntityPermission } from '@shared/types/permissions';
import type { TableUserPermissionContext } from '../../components/common/DynamicTable/types';
import { getCurrentUser } from '../../utils/jwtUtils';
import { hasDomainPermission } from '../../utils/permissionHelpers';

const DEFAULT_SYSTEM_ACCESS: TableUserPermissionContext['systemAccess'] = {
  businessSystem: true,
  adminPanel: false,
  reportingTools: false,
  developerTools: false,
};

function buildDefaultDomainPermissions(
  businessDomain: BusinessDomain | string
): InternalEntityPermission[] {
  return [
    `${businessDomain}:read`,
    `${businessDomain}:create`,
    `${businessDomain}:update`,
    `${businessDomain}:delete`,
  ] as InternalEntityPermission[];
}

/**
 * DynamicTable용 사용자 권한 컨텍스트를 JWT에서 파생한다.
 */
export interface UseTablePermissionContextOptions {
  /**
   * UI read-only 강제 (예: flow_event/stream_event 로그 목록).
   * Preset SSOT: `DynamicTable/utils/domainTableDefaults.ts` — LOG_READ_ONLY_DOMAINS.
   */
  readOnly?: boolean;
}

export function useTablePermissionContext(
  businessDomain: BusinessDomain | string,
  options?: UseTablePermissionContextOptions
): TableUserPermissionContext {
  const currentUser = useMemo(() => getCurrentUser(), []);
  const userId = currentUser?.userId || 'anonymous';

  const userPermissions = useMemo(() => {
    if (currentUser?.permissions && Array.isArray(currentUser.permissions)) {
      return currentUser.permissions;
    }
    return [];
  }, [currentUser]);

  return useMemo(() => {
    const domain = businessDomain as BusinessDomain;
    const defaultPermissions = buildDefaultDomainPermissions(businessDomain);
    const effectivePermissions = (
      userPermissions.length > 0 ? userPermissions : defaultPermissions
    ) as InternalEntityPermission[];

    const permissionContextBase = {
      domainPermissions: [
        {
          domain,
          level: 'read-write' as const,
          permissions: effectivePermissions,
        },
      ],
      globalPermissions: effectivePermissions,
    };

    const hasCreate = hasDomainPermission(permissionContextBase, domain, 'create');
    const hasUpdate = hasDomainPermission(permissionContextBase, domain, 'update');
    const hasDelete = hasDomainPermission(permissionContextBase, domain, 'delete');

    const permissionLevel: 'read-only' | 'read-write' = options?.readOnly
      ? 'read-only'
      : hasCreate && hasUpdate && hasDelete
        ? 'read-write'
        : 'read-only';

    const readPermission = `${businessDomain}:read` as InternalEntityPermission;
    const scopedPermissions = options?.readOnly
      ? ([readPermission] as InternalEntityPermission[])
      : effectivePermissions;

    return {
      userId,
      domainPermissions: [
        {
          domain,
          level: permissionLevel,
          permissions: scopedPermissions,
        },
      ],
      globalPermissions: scopedPermissions,
      systemAccess: DEFAULT_SYSTEM_ACCESS,
    };
  }, [userPermissions, userId, businessDomain, options?.readOnly]);
}
